// Part 2 (Lane D): getting a locked-out player back into a started game.
//
// Two routes, both ending with the SAME player_id holding a FRESH claim token
// (the old phone's token stops working; answers and score stay):
//   1. The player asks. Their phone files request_readmit with a secret only
//      it holds, shows a 4-character code, and polls for the answer. The GM
//      types that code on their own phone (approve_readmit). A stranger who
//      sees the code can't use it: approval happens on the GM's phone, and
//      only the phone holding the request secret can collect the token.
//   2. The GM gives a code. issue_readmit_code shows 8 digits on the GM's
//      phone; the player types them (redeem_readmit_code).
//
// This module holds the RPC wrappers, input normalisation, the request
// store and the status poller. No React here; hooks live in hooks/useReadmit.js.
// Contract: scratchpad/part2/CONTRACT.md §4.7.

import { supabase } from '@tasted/utils/supabase';
import { randomUUID } from '@tasted/utils/randomId';
import { playerStorage, claimTokenStorage, gmSecretStorage } from './playerStorage';
import { readmitStatusOutcome, errorCode } from './rejoinErrors';

export const READMIT_POLL_MS = 3000;
export const READMIT_POLL_MAX_MS = 15000;
export const FEATURE_TIMEOUT_MS = 4000;
export const RPC_TIMEOUT_MS = 10000;

export const FEATURE_KEYS = Object.freeze([
  'room_step_authority',
  'room_active_seats',
  'room_help_sheet',
  'room_votes',
  'room_readmit',
  'room_restart_question',
  'room_reset_session',
]);

export const ALL_FEATURES_OFF = Object.freeze(
  FEATURE_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {})
);

/** Reject after `ms` with an error the copy map reads as a network problem. */
export function withTimeout(promise, ms = RPC_TIMEOUT_MS) {
  let timer;
  return Promise.race([
    Promise.resolve(promise).finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('request timed out — network stalled')), ms);
    }),
  ]);
}

/** Supabase call → { data, error }, never throws. A throw becomes a network-shaped error. */
async function call(run, ms = RPC_TIMEOUT_MS) {
  try {
    const res = await withTimeout(run(), ms);
    return { data: res?.data ?? null, error: res?.error ?? null };
  } catch (e) {
    return { data: null, error: { message: e?.message || 'network error', code: '' } };
  }
}

// ── Feature flags ──────────────────────────────────────────────────────────

/**
 * Per-session Part 2 switches (game_session_features). Any failure — the RPC
 * missing because migration 211 isn't applied, a timeout, a bad body — reads
 * as everything OFF, which is today's behaviour. Production ships dark.
 */
export async function fetchSessionFeatures(sessionCode, { timeoutMs = FEATURE_TIMEOUT_MS } = {}) {
  if (!sessionCode) return ALL_FEATURES_OFF;
  const { data, error } = await call(
    () => supabase.rpc('game_session_features', { p_session_id: sessionCode }),
    timeoutMs
  );
  if (error || !data || typeof data !== 'object') return ALL_FEATURES_OFF;
  return FEATURE_KEYS.reduce((acc, k) => ({ ...acc, [k]: data[k] === true }), {});
}

/**
 * The GM's nickname, for "Show {GM} this code". One name, the one the player
 * is about to walk over to anyway; null when it can't be read (the panel then
 * says "the host").
 */
export async function fetchGmName(sessionCode) {
  if (!sessionCode) return null;
  const { data, error } = await call(
    () =>
      supabase
        .from('session_users_v2')
        .select('username')
        .eq('session_id', sessionCode)
        .eq('is_gamemaster', true)
        .maybeSingle(),
    FEATURE_TIMEOUT_MS
  );
  if (error || !data?.username) return null;
  return String(data.username);
}

// ── Code input ─────────────────────────────────────────────────────────────

// Crockford base-32 without I L O U: what request_readmit's display codes use.
const DISPLAY_CODE_RE = /^[0-9A-HJKMNP-TV-Z]{4}$/;

/**
 * What the GM typed → the 4-character display code. Case and spaces don't
 * matter; O/I/L are read as 0/1/1 (they never appear in a display code, so a
 * GM who types the look-alike letter still gets it right).
 */
export function normalizeDisplayCode(raw) {
  return String(raw ?? '')
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
}

export function isValidDisplayCode(raw) {
  return DISPLAY_CODE_RE.test(normalizeDisplayCode(raw));
}

/** The host's 8-digit code, spaces and dashes ignored. */
export function normalizeHostCode(raw) {
  return String(raw ?? '').replace(/[\s-]/g, '');
}

export function isValidHostCode(raw) {
  return /^\d{8}$/.test(normalizeHostCode(raw));
}

/** '48210937' → '4821 0937'. */
export function formatHostCode(code) {
  const c = normalizeHostCode(code);
  return c.length === 8 ? `${c.slice(0, 4)} ${c.slice(4)}` : c;
}

// ── Request store (this phone only) ───────────────────────────────────────

// The request secret is what lets THIS phone collect the token once the GM
// approves. It lives in memory and in storage, keyed by game and request id.
//
// A10 (edge-case audit 2026-09-28): it used to be sessionStorage, so closing
// the tab — or iOS dropping it — lost a request the host hadn't answered yet,
// and the player had to ask again. It now uses the store the rest of this
// phone's identity lives in (localStorage in production; sessionStorage in
// DEV, so test windows stay apart — same split as playerStorage). The secret
// never leaves this phone either way. Entries past their collect window are
// swept on every save and read, so the store doesn't grow across games.
const memoryRequests = new Map();
const memoryLatest = new Map();
const REQ_PREFIX = 'readmitReq:';
const reqKey = (code, id) => `${REQ_PREFIX}${code}:${id}`;
const latestKey = (code) => `readmitLatest:${code}`;
// An approved token is collectable for 10 minutes after approval, and the
// request itself can't be approved after expires_at — keep it that long.
const COLLECT_WINDOW_MS = 10 * 60 * 1000;
// A request saved without an expires_at (the server always sends one).
const NO_EXPIRY_TTL_MS = 60 * 60 * 1000;

function identityStore() {
  try {
    if (typeof window === 'undefined') return null;
    return import.meta.env.DEV ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function isPastCollectWindow(entry, now) {
  const expires = Date.parse(entry?.expiresAt || '');
  if (Number.isFinite(expires)) return now > expires + COLLECT_WINDOW_MS;
  const saved = Number(entry?.savedAt);
  return Number.isFinite(saved) && now > saved + NO_EXPIRY_TTL_MS;
}

/** Remove every stored request (any game) whose collect window has passed. */
function sweepExpired(now = Date.now()) {
  const st = identityStore();
  if (!st) return;
  try {
    const doomed = [];
    for (let i = 0; i < st.length; i += 1) {
      const key = st.key(i);
      if (!key || !key.startsWith(REQ_PREFIX)) continue;
      let entry = null;
      try { entry = JSON.parse(st.getItem(key) || 'null'); } catch { entry = null; }
      if (!entry || !entry.requestId || isPastCollectWindow(entry, now)) doomed.push({ key, entry });
    }
    for (const { key, entry } of doomed) {
      st.removeItem(key);
      const code = entry?.sessionCode;
      if (code && st.getItem(latestKey(code)) === entry.requestId) st.removeItem(latestKey(code));
    }
  } catch { /* best effort */ }
}

export const readmitRequestStore = {
  save(entry) {
    if (!entry?.requestId || !entry?.secret) return;
    const stored = { ...entry, savedAt: entry.savedAt ?? Date.now() };
    memoryRequests.set(entry.requestId, stored);
    if (entry.sessionCode) memoryLatest.set(entry.sessionCode, entry.requestId);
    sweepExpired();
    if (!entry.sessionCode) return; // nothing to key it by; memory only
    try {
      identityStore()?.setItem(reqKey(entry.sessionCode, entry.requestId), JSON.stringify(stored));
      identityStore()?.setItem(latestKey(entry.sessionCode), entry.requestId);
    } catch { /* memory mirror still holds it */ }
  },
  load(requestId, sessionCode) {
    if (!requestId) return null;
    if (memoryRequests.has(requestId)) return memoryRequests.get(requestId);
    if (!sessionCode) return null;
    try {
      const raw = identityStore()?.getItem(reqKey(sessionCode, requestId));
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && parsed.requestId === requestId && parsed.secret ? parsed : null;
    } catch { return null; }
  },
  /** The most recent request this phone filed for a game, if it is still worth polling. */
  latestFor(sessionCode, { now = Date.now() } = {}) {
    if (!sessionCode) return null;
    sweepExpired(now);
    let id = memoryLatest.get(sessionCode);
    if (!id) {
      try { id = identityStore()?.getItem(latestKey(sessionCode)) || null; } catch { id = null; }
    }
    const entry = id ? this.load(id, sessionCode) : null;
    if (!entry || entry.sessionCode !== sessionCode) return null;
    if (isPastCollectWindow(entry, now)) {
      this.clear(entry.requestId, sessionCode);
      return null;
    }
    return entry;
  },
  clear(requestId, sessionCode) {
    if (requestId) memoryRequests.delete(requestId);
    if (sessionCode && memoryLatest.get(sessionCode) === requestId) memoryLatest.delete(sessionCode);
    try {
      const st = identityStore();
      if (requestId && sessionCode) st?.removeItem(reqKey(sessionCode, requestId));
      if (sessionCode && st?.getItem(latestKey(sessionCode)) === requestId) st?.removeItem(latestKey(sessionCode));
    } catch { /* best effort */ }
  },
};

export function __resetReadmitForTests() {
  memoryRequests.clear();
  memoryLatest.clear();
}

// ── Player-side RPCs ───────────────────────────────────────────────────────

/**
 * File a readmit request. Returns { data: { requestId, displayCode, expiresAt,
 * secret }, error }. The secret is generated here and never leaves this phone
 * except in the request itself and the status polls.
 */
export async function requestReadmit({ sessionCode, username, playerId = null, kind = 'readmit' }) {
  const secret = randomUUID();
  // kind 'late_join' (migration 233, audit E1): a new person asking to join a
  // started game. Same code, same poll; the seat only exists once the GM says yes.
  const { data, error } = await call(() =>
    supabase.rpc(kind === 'late_join' ? 'request_late_join' : 'request_readmit', {
      p_session_id: sessionCode,
      p_username: username,
      p_request_secret: secret,
    })
  );
  if (error) return { data: null, error };
  if (!data?.request_id || !data?.display_code) {
    return { data: null, error: { message: 'unexpected response', code: 'UNEXPECTED' } };
  }
  const entry = {
    requestId: data.request_id,
    secret,
    sessionCode,
    username,
    playerId,
    displayCode: String(data.display_code),
    expiresAt: data.expires_at || null,
    kind,
  };
  readmitRequestStore.save(entry);
  return { data: entry, error: null };
}

export function readmitRequestStatus({ requestId, secret }) {
  return call(() =>
    supabase.rpc('readmit_request_status', { p_request_id: requestId, p_request_secret: secret })
  );
}

export function redeemReadmitCode({ sessionCode, username, code }) {
  return call(() =>
    supabase.rpc('redeem_readmit_code', {
      p_session_id: sessionCode,
      p_username: username,
      p_code: normalizeHostCode(code),
    })
  );
}

export function reactivateMySeat({ sessionCode, playerId, claimToken }) {
  return call(() =>
    supabase.rpc('reactivate_my_seat', {
      p_session_id: sessionCode,
      p_player_id: playerId,
      p_claim_token: claimToken,
    })
  );
}

/**
 * Store what an approval or a redeemed code handed back. Refuses a token for
 * a different seat than the one this phone asked about: the server should
 * never do that, and if it ever did, this phone must not quietly become
 * someone else. Returns true when the identity is now this phone's.
 */
export function adoptReadmittedSeat({ playerId, claimToken, expectedPlayerId = null, sessionCode }) {
  if (!playerId || !claimToken) return false;
  if (expectedPlayerId && playerId !== expectedPlayerId) return false;
  // Write-through: both setters mirror in memory before the store, so a
  // refusing store doesn't undo an approval the server already granted.
  // Storing the token also releases answers the queue parked on the old one
  // (A9). The id is keyed by game (A14); without a code, the URL's is used.
  claimTokenStorage.set(playerId, claimToken);
  playerStorage.set(playerId, sessionCode);
  return true;
}

// ── Status poller ──────────────────────────────────────────────────────────

/**
 * Poll readmit_request_status until the request ends. Realtime can't carry
 * this: anon can't read the secrets table, and the token must only ever go to
 * the phone holding the secret.
 *
 * Every 3 s while pending. Errors that aren't the end of the request (network,
 * a 5xx) back off, doubling up to 15 s, and reset on the next good answer.
 * While the tab is hidden it polls at the slow rate; `pokeNow()` polls at once
 * (call it on visibilitychange / online).
 *
 * Callbacks:
 *   onApproved({ player_id, claim_token, token_expires_at })  — stop after
 *   onFinished(outcome)   — declined / expired / delivered: the request is over
 *   onTrouble(outcome)    — a transient failure; still polling
 *   onPending(data)       — still waiting (clears any trouble shown)
 */
export function startReadmitPolling({
  requestId,
  secret,
  onApproved,
  onFinished,
  onTrouble,
  onPending,
  fetchStatus = readmitRequestStatus,
  baseMs = READMIT_POLL_MS,
  maxMs = READMIT_POLL_MAX_MS,
  isHidden = () => (typeof document !== 'undefined' ? document.hidden === true : false),
}) {
  let stopped = false;
  let timer = null;
  let failures = 0;
  let inFlight = false;

  const schedule = (ms) => {
    if (stopped) return;
    clearTimeout(timer);
    timer = setTimeout(tick, ms);
  };

  async function tick() {
    if (stopped || inFlight) return;
    inFlight = true;
    let res;
    try {
      res = await fetchStatus({ requestId, secret });
    } catch (e) {
      res = { data: null, error: { message: e?.message || 'network error', code: '' } };
    } finally {
      inFlight = false;
    }
    if (stopped) return;

    const { data, error } = res || {};
    if (error) {
      const outcome = readmitStatusOutcome({ error });
      if (outcome && outcome.kind === 'expired') {
        stopped = true;
        onFinished?.(outcome);
        return;
      }
      failures += 1;
      onTrouble?.(outcome);
      schedule(Math.min(maxMs, baseMs * 2 ** failures));
      return;
    }

    failures = 0;
    const status = data?.status;
    if (status === 'approved') {
      if (data.player_id && data.claim_token) {
        stopped = true;
        onApproved?.(data);
        return;
      }
      // Approved but no token in the body: poll again rather than guess.
      schedule(baseMs);
      return;
    }
    const outcome = readmitStatusOutcome({ status });
    if (outcome) {
      stopped = true;
      onFinished?.(outcome);
      return;
    }
    onPending?.(data);
    schedule(isHidden() ? maxMs : baseMs);
  }

  schedule(0);

  return {
    stop() {
      stopped = true;
      clearTimeout(timer);
    },
    pokeNow() {
      if (stopped || inFlight) return;
      schedule(0);
    },
    get stopped() {
      return stopped;
    },
  };
}

// ── GM-side RPCs ───────────────────────────────────────────────────────────

/**
 * The GM's credentials, from this phone's storage unless given. Every GM
 * readmit power requires the GM secret (contract change 2026-09-25, item 1).
 */
export function gmCredentials(sessionCode, overrides = {}) {
  const actorPlayerId = overrides.actorPlayerId ?? playerStorage.get();
  return {
    actorPlayerId,
    claimToken: overrides.claimToken ?? claimTokenStorage.get(actorPlayerId),
    gmSecret: overrides.gmSecret ?? gmSecretStorage.get(sessionCode),
  };
}

async function claimGmSecret(sessionCode, actorPlayerId, claimToken) {
  const { data, error } = await call(() =>
    supabase.rpc('claim_gm_secret_secure', {
      p_session_id: sessionCode,
      p_player_id: actorPlayerId,
      p_claim_token: claimToken,
    })
  );
  if (error || !data) return null;
  try { gmSecretStorage.set(sessionCode, data); } catch { /* in-memory use below still works */ }
  return data;
}

/**
 * Run a GM RPC with the secret this phone holds. A phone that was just made
 * GM may not have claimed one yet (every GM change deletes it server-side), so
 * a missing secret, or a 42501 with the one we hold, re-claims once and
 * retries. approve_readmit RETURNS its wrong-code result instead of raising, so
 * a retry after 42501 never counts a wrong code twice.
 */
async function withGmSecret(sessionCode, creds, run) {
  let secret = creds.gmSecret || (await claimGmSecret(sessionCode, creds.actorPlayerId, creds.claimToken));
  let res = await call(() => run(secret));
  if (res.error && errorCode(res.error) === '42501') {
    const fresh = await claimGmSecret(sessionCode, creds.actorPlayerId, creds.claimToken);
    if (fresh && fresh !== secret) {
      secret = fresh;
      res = await call(() => run(secret));
    }
  }
  return res;
}

export function approveReadmit({ sessionCode, requestId, code, credentials }) {
  const creds = gmCredentials(sessionCode, credentials);
  return withGmSecret(sessionCode, creds, (secret) =>
    supabase.rpc('approve_readmit', {
      p_session_id: sessionCode,
      p_actor_player_id: creds.actorPlayerId,
      p_claim_token: creds.claimToken,
      p_request_id: requestId,
      p_code: normalizeDisplayCode(code),
      p_gm_secret: secret || null,
    })
  );
}

export function declineReadmit({ sessionCode, requestId, credentials }) {
  const creds = gmCredentials(sessionCode, credentials);
  return withGmSecret(sessionCode, creds, (secret) =>
    supabase.rpc('decline_readmit', {
      p_session_id: sessionCode,
      p_actor_player_id: creds.actorPlayerId,
      p_claim_token: creds.claimToken,
      p_request_id: requestId,
      p_gm_secret: secret || null,
    })
  );
}

export function issueReadmitCode({ sessionCode, targetPlayerId, credentials }) {
  const creds = gmCredentials(sessionCode, credentials);
  return withGmSecret(sessionCode, creds, (secret) =>
    supabase.rpc('issue_readmit_code', {
      p_session_id: sessionCode,
      p_actor_player_id: creds.actorPlayerId,
      p_claim_token: creds.claimToken,
      p_target_player_id: targetPlayerId,
      p_gm_secret: secret || null,
    })
  );
}

/**
 * "Mark {name} away" from a readmit row, after approve_readmit refused
 * because the seat is still live. `needs_approval` is set: the GM is deciding
 * which phone holds this seat, so the old phone can't quietly tap "I'm back"
 * between this and the approval that follows.
 */
export function markSeatAway({ sessionCode, targetPlayerId, credentials }) {
  const creds = gmCredentials(sessionCode, credentials);
  return withGmSecret(sessionCode, creds, (secret) =>
    supabase.rpc('set_seat_active', {
      p_session_id: sessionCode,
      p_actor_player_id: creds.actorPlayerId,
      p_claim_token: creds.claimToken,
      p_target_player_id: targetPlayerId,
      p_active: false,
      p_needs_approval: true,
      p_gm_secret: secret || null,
    })
  );
}

// ── Request rows (GM side, from realtime on session_readmit_requests) ──────

/** A request from someone new to the game (migration 233), not a returning seat. */
export function isLateJoinRequest(row) {
  return row?.kind === 'late_join';
}

/** The name to show for a request row: the seat's name, or the new player's. */
export function readmitRequestName(row, roster = []) {
  if (isLateJoinRequest(row)) return row?.requested_name || null;
  return (Array.isArray(roster) ? roster : []).find((p) => p.id === row?.player_id)?.username || null;
}

/** A pending row whose expires_at has passed is expired; the server marks it lazily. */
export function isReadmitRequestLive(row, now = Date.now()) {
  if (!row || row.status !== 'pending') return false;
  const exp = Date.parse(row.expires_at || '');
  return !Number.isFinite(exp) || exp > now;
}

/** Live pending requests, oldest first, at most one row per request id. */
export function liveReadmitRequests(rows, now = Date.now()) {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => isReadmitRequestLive(r, now) && !seen.has(r.id) && seen.add(r.id))
    .sort((a, b) => Date.parse(a.created_at || 0) - Date.parse(b.created_at || 0));
}
